/**
 * Tunnels de conversion (admin) — RPC `admin_conversion_funnels` (migration
 * 0718) : chaque tunnel suit les MÊMES visiteurs étape par étape, dans l'ordre
 * (une étape ne compte que si elle vient après la précédente). Période en
 * cours et période d'avant de même durée, fusionnées ici.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { StatsRange } from './live-stats';

/** Inscription, achat, ou invitations et défis partagés (0724). */
export type ConversionGroup = 'entry' | 'buy' | 'invite';

export interface RawFunnel {
  key: string;
  label: string;
  group: ConversionGroup;
  new_only?: boolean;
  steps: { key: number; label: string; count: number }[];
}

export interface ConversionStep {
  label: string;
  count: number;
  prev: number;
  /** Part des visiteurs de la 1re étape (0..1). */
  ofFirst: number;
  /** Part des visiteurs de l'étape précédente (null pour la 1re). */
  ofPrevStep: number | null;
  /** Visiteurs perdus depuis l'étape précédente. */
  lost: number;
}

export interface ConversionFunnel {
  key: string;
  label: string;
  group: ConversionGroup;
  /** Nouveaux visiteurs seulement (un compte déjà créé ne compte pas). */
  newOnly: boolean;
  steps: ConversionStep[];
  /** Taux de bout en bout (dernière étape ÷ 1re), null si personne au départ. */
  rate: number | null;
  prevRate: number | null;
  started: number;
  converted: number;
}

function ratio(part: number, base: number): number | null {
  return base > 0 ? part / base : null;
}

/** Tunnels de la période, avec les chiffres de la période d'avant. */
export function mergeFunnels(cur: RawFunnel[], prev: RawFunnel[]): ConversionFunnel[] {
  return cur.map((f) => {
    const before = prev.find((p) => p.key === f.key);
    const steps = [...f.steps].sort((a, b) => a.key - b.key);
    const prevSteps = before ? [...before.steps].sort((a, b) => a.key - b.key) : [];
    const first = steps[0]?.count ?? 0;
    const last = steps[steps.length - 1]?.count ?? 0;
    const pFirst = prevSteps[0]?.count ?? 0;
    const pLast = prevSteps[prevSteps.length - 1]?.count ?? 0;
    return {
      key: f.key,
      label: f.label,
      group: f.group,
      newOnly: f.new_only === true,
      started: first,
      converted: last,
      rate: ratio(last, first),
      prevRate: ratio(pLast, pFirst),
      steps: steps.map((s, i) => {
        const before = i > 0 ? steps[i - 1]!.count : null;
        return {
          label: s.label,
          count: s.count,
          prev: prevSteps.find((p) => p.key === s.key)?.count ?? 0,
          ofFirst: first > 0 ? s.count / first : 0,
          ofPrevStep: before == null ? null : before > 0 ? s.count / before : 0,
          lost: before == null ? 0 : Math.max(0, before - s.count),
        };
      }),
    };
  });
}

const PCT = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/** « 12 % », « 3,4 % », « 0,2 % » ; « — » sans base. */
export function formatRate(rate: number | null): string {
  if (rate == null) return '—';
  const pct = rate * 100;
  return `${PCT.format(pct >= 10 ? Math.round(pct) : pct)}\u00a0%`;
}

/** Écart de taux en points : « +1,2 pt », « −0,5 pt », « = », null sans comparaison. */
export function pointsDelta(
  rate: number | null,
  prevRate: number | null,
): { label: string; dir: 'up' | 'down' | 'flat' } | null {
  if (rate == null || prevRate == null) return null;
  const pts = Math.round((rate - prevRate) * 1000) / 10;
  if (pts === 0) return { label: '=', dir: 'flat' };
  const abs = PCT.format(Math.abs(pts));
  return pts > 0
    ? { label: `+${abs}\u00a0pt`, dir: 'up' }
    : { label: `−${abs}\u00a0pt`, dir: 'down' };
}

/** L'étape qui perd le plus de visiteurs (en part), pour la signaler. */
export function biggestDrop(f: Pick<ConversionFunnel, 'steps'>): number | null {
  let worst: number | null = null;
  let worstRate = Infinity;
  f.steps.forEach((s, i) => {
    if (i === 0 || s.ofPrevStep == null || f.steps[i - 1]!.count === 0) return;
    if (s.ofPrevStep < worstRate) {
      worstRate = s.ofPrevStep;
      worst = i;
    }
  });
  return worst;
}

export interface ConversionFunnelsData {
  range: StatsRange;
  from: string;
  funnels: ConversionFunnel[];
}

/** Admin : tunnels, rechargés toutes les 60 s. */
export function useConversionFunnels(range: StatsRange, enabled: boolean) {
  return useQuery({
    queryKey: ['admin-conversion-funnels', range],
    enabled,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ConversionFunnelsData> => {
      const db = supabase as unknown as SupabaseClient;
      const { data, error } = await db.rpc('admin_conversion_funnels', { p_range: range });
      if (error) throw error;
      const d = data as { range: StatsRange; from: string; cur: RawFunnel[]; prev: RawFunnel[] };
      return { range: d.range, from: d.from, funnels: mergeFunnels(d.cur ?? [], d.prev ?? []) };
    },
  });
}
